import Divider from "antd/es/divider";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src";
import styled from "styled-components";
import { formatCents } from "@homero/pricing";
import { CartContainer } from ".";
import { getTotalPrice } from "../../actions";
import { usePricingConfig } from "../../hooks/usePricingConfig";
import { FEE, SAVINGS } from "./savings";
import { appointmentValueCentsOf, cartSummary, discountLinesFor } from "../../lib/price";
import {
  AnimatedCents,
  AnimatedPercent,
  DiscountLine,
  useFlash,
  useLedgerRows,
  usePrefersReducedMotion,
  useRollingTotal,
} from "./cart-motion";

export const Cart = (props) => {
  const dispatch = useDispatch();
  const booking = useSelector((state: RootState) => state.bookingReducer);
  const { occurance, code, priceNotice } = booking;

  /**
   * Subscribes the cart to the rates arriving from the API. Without it the cart
   * would keep showing the bundled fallback prices until some other state change
   * happened to re-render it.
   */
  usePricingConfig();

  /**
   * Credits, tip and the name the total goes by, from the one place that knows
   * them — shared with the sticky bar that stands in for this cart wherever
   * the layout puts it below the fold.
   */
  const {
    breakdown,
    tipCents,
    creditsCents,
    creditsAppliedCents,
    amountDueCents,
    hasServiceDate,
    ongoingCents,
    totalLabel,
  } = cartSummary(booking);

  /**
   * Every line below comes from one breakdown, in cents.
   *
   * Previously each was interpolated raw, so a decimal amount rendered with the
   * trailing zero dropped — "-$32.2" and "$ 115.8" instead of "-$32.20" and
   * "$115.80". Invisible while prices were whole dollars; immediately visible
   * once they carry cents. The Discount line also recomputed itself from
   * scratch rather than reading the breakdown, making it a sixth independent
   * implementation of the same arithmetic.
   */
  /**
   * Priced from the whole store, the same input `get_total_price` hands the
   * engine for the `total_amount` posted at checkout. This cart used to build
   * its own field-by-field literal, and every field it missed — the date's
   * surge, then Same-Day — rendered a total the card was not charged. Handing
   * over the whole slice is what keeps the two from disagreeing again.
   */
  /**
   * Surge folded in, exactly as `toBookingLines` does it.
   *
   * The cart used to render `baseCents` alone, so the moment a day carried a
   * surge its lines stopped adding up: appointment value minus the discounts
   * came out short of the subtotal by precisely the surge, with no line to
   * account for the gap. Invisible while every day was 0%, wrong the day an
   * admin set one.
   *
   * Folding rather than itemising is the deliberate rule — surge is what the
   * appointment costs on that day, not a fee bolted onto it (`CONTEXT.md`, and
   * the admin editor tells the operator the customer "sees one final price").
   * Same-Day is the opposite and gets its own line above, because the customer
   * chose it by name.
   */
  const appointmentValueCents = appointmentValueCentsOf(breakdown);

  /**
   * The same three components the Discount line sums, itemised.
   *
   * The line has always collapsed a plan discount, the flexible-window credit
   * and a promo code into one number, so a customer who applied a code could
   * not tell how much of the saving was the code and how much was the plan they
   * were about to be billed on every visit. These read from the breakdown, so
   * the parts add up to the number beside them by construction.
   */
  /*
    Built by `discountLinesFor` rather than here, so the summary behind the
    phone's sticky bar prints the same named savings in the same order. The
    list used to live in this component, which made it unreachable from the one
    surface that needs it most: on steps one to three a phone has no sidebar
    and so no cart at all.
  */
  const discountLines: DiscountLine[] = discountLinesFor(
    breakdown,
    occurance,
    code
  );

  const discountCents = discountLines.reduce((sum, line) => sum + line.cents, 0);

  /**
   * The blended rate off the appointment value — the line directly above this
   * one — so the two numbers in this row are a rate and an amount rather than
   * the same fact twice. It moves as the customer configures, which is the
   * point: it is the number they are actually shopping on.
   */
  const discountPercent = appointmentValueCents
    ? Math.round((discountCents / appointmentValueCents) * 100)
    : 0;

  /**
   * Push the recomputed total into the store whenever the *price* changes.
   *
   * Keyed on the breakdown's own total rather than on a hand-listed set of
   * inputs. The old list — `[subTotal, totalPrice, codeDiscount]` — named none of
   * the fields that decide surge, so picking a date changed the number on screen
   * while `state.totalPrice` kept its pre-date value. `weirdStuff/api.ts` posts
   * `total_amount: state.totalPrice`, so the customer read a surged total and the
   * server was asked to charge the unsurged one.
   *
   * Any dependency list spelled out by hand has to be re-audited every time a new
   * input to pricing is added, and was already wrong twice. Watching the computed
   * total instead makes the displayed price and the stored price the same fact by
   * construction. It cannot loop: the dispatch writes `subTotal`/`totalPrice`,
   * neither of which feeds `breakdown`, so the recomputed total is unchanged and
   * the effect does not re-fire.
   */
  React.useEffect(() => {
    dispatch(getTotalPrice());
  }, [breakdown.totalCents, dispatch]);

  /**
   * The motion grammar. `useLedgerRows` hands back the priced lines plus any
   * that have just stopped being priced, held for one EXIT so a saving the
   * customer gave up collapses out rather than blinking away.
   */
  const reducedMotion = usePrefersReducedMotion();
  const ledgerRows = useLedgerRows(discountLines, reducedMotion);

  const discountRowRef = React.useRef<HTMLDivElement>(null);
  const sameDayRowRef = React.useRef<HTMLParagraphElement>(null);
  const travelRowRef = React.useRef<HTMLParagraphElement>(null);
  const totalRowRef = React.useRef<HTMLSpanElement>(null);

  /**
   * Green washes a saving, amber washes a charge, and the two are never
   * swapped: `savings.ts` has said since it was written that green means
   * "costs less", and only that.
   */
  useFlash(discountRowRef, discountCents, "washing", !reducedMotion && discountCents > 0);
  useFlash(
    sameDayRowRef,
    breakdown.sameDaySurchargeCents,
    "washing-fee",
    !reducedMotion && breakdown.sameDaySurchargeCents > 0
  );
  useFlash(
    travelRowRef,
    breakdown.travelPremiumCents,
    "washing-fee",
    !reducedMotion && breakdown.travelPremiumCents > 0
  );

  useRollingTotal(totalRowRef, amountDueCents, !reducedMotion);

  return (
    <CartContainer data-testid="cart">
      <>
        <p className="flex justify-content-between">
          <span>Appointment Value:</span>
          <span className="blue">
            <AnimatedCents cents={appointmentValueCents} />
          </span>
        </p>
        {breakdown.sameDaySurchargeCents > 0 ? (
          <p className="flex justify-content-between" ref={sameDayRowRef}>
            <span>Same-Day service:</span>
            <span className="blue">
              <AnimatedCents cents={breakdown.sameDaySurchargeCents} prefix="+" />
            </span>
          </p>
        ) : null}
        {breakdown.travelPremiumCents > 0 ? (
          /*
            Its own line, like Same-Day: a charge the customer can avoid by
            picking another day, so it is named rather than folded in. Amber,
            like every other place a fee is shown.
          */
          <p className="flex justify-content-between" ref={travelRowRef}>
            <span>Travel fee:</span>
            <span className="blue" style={{ color: FEE.ink }}>
              <AnimatedCents cents={breakdown.travelPremiumCents} prefix="+" />
            </span>
          </p>
        ) : null}
        {discountCents > 0 ? (
          <DiscountBlock ref={discountRowRef}>
            <p className="flex justify-content-between discount-head">
              <span>
                Discount:{" "}
                {/*
                  A summary now, not a control. The itemisation it used to hide
                  is directly underneath, so there is nothing left to open — and
                  a pill that opened nothing was still a tab stop and still
                  invited a click on the phone, where the cart is the only price
                  surface there is.
                */}
                <SavingsPill>
                  <AnimatedPercent value={discountPercent} prefix={"\u2212"} />
                </SavingsPill>
              </span>
              <span className="blue">
                <AnimatedCents cents={discountCents} prefix={"\u2212"} />
              </span>
            </p>
            {/*
              The ledger, on the face of the card rather than inside a tooltip.
              Hovering was the only way to learn how much of a saving was the
              plan the customer is about to be billed on every visit and how
              much was the code they typed once — unreachable on a phone without
              a deliberate tap on a 0.82rem pill, and impossible to animate,
              because nothing can announce a change nobody can see.
            */}
            <Ledger aria-label="What makes up your discount">
              {ledgerRows.map((line) => (
                <li
                  key={line.tag}
                  data-savings-line={line.tag}
                  data-exiting={line.exiting ? "true" : undefined}
                  aria-hidden={line.exiting ? true : undefined}
                  className={line.entering ? "entering" : line.exiting ? "exiting" : undefined}
                  style={line.delayMs ? { animationDelay: `${line.delayMs}ms` } : undefined}
                >
                  <span className="line">
                    <em>{line.tag}</em>
                    <span className="label">{line.label}</span>
                    <b>
                      <AnimatedCents cents={line.cents} prefix={"\u2212"} />
                    </b>
                  </span>
                </li>
              ))}
            </Ledger>
          </DiscountBlock>
        ) : null}

        {breakdown.addOnsCents > 0 ? (
          <p className="flex justify-content-between">
            <span>Add Ons:</span>
            <span className="blue">
              <AnimatedCents cents={breakdown.addOnsCents} prefix="+" />
            </span>
          </p>
        ) : null}
        <Divider style={{ color: "#B1DCE5" }} />
      </>
      <p className="flex justify-content-between">
        <span className="blue">Subtotal:</span>
        <span className="blue">
          <AnimatedCents cents={breakdown.subTotalCents} />
        </span>
      </p>

      <p className="flex justify-content-between">
        <span className="blue">Tax:</span>
        <span className="blue">
          <AnimatedCents cents={breakdown.taxCents} />
        </span>
      </p>

      <p className="flex justify-content-between">
        <span className="blue">Tip:</span>
        <span className="blue">
          <AnimatedCents cents={tipCents} />
        </span>
      </p>

      <Divider style={{ color: "#B1DCE5" }} />

      {creditsCents > 0 ? (
        <>
          <p className="flex justify-content-between">
            <span className="blue">Credits:</span>
            <span className="blue">
              {formatCents(creditsAppliedCents)} (
              {formatCents(creditsCents - creditsAppliedCents)} remaining)
            </span>
          </p>
          <Divider style={{ color: "#B1DCE5" }} />
        </>
      ) : null}

      <p className="flex justify-content-between">
        <span className="blue">
          {/*
            "First cleaning" once a plan's first visit is discounted and its
            later ones are not, because "Total" beside one of two different
            numbers is the sentence a customer reads as the price of every
            cleaning — and then disputes on the second invoice.
          */}
          {totalLabel}
        </span>
        <span className="blue">
          {/*
            The total the customer was shown before checkout moved it, struck
            through beside the new one, for as long as the notice explaining
            why is on screen.
          */}
          {priceNotice &&
          !booking.proximityPending &&
          priceNotice.reason !== "checked" &&
          priceNotice.reason !== "mismatch" &&
          priceNotice.wasTotalCents !== breakdown.totalCents ? (
            // #5c618a: 4.8:1 on the cart's cyan, where #7c80a8 was 3.4:1.
            <s style={{ fontWeight: 400, color: "#5c618a", marginRight: 8 }}>
              {/* After credits, like the number beside it. */}
              {formatCents(
                priceNotice.wasTotalCents +
                  tipCents -
                  Math.min(creditsCents, priceNotice.wasTotalCents + tipCents)
              )}
            </s>
          ) : null}
          <span ref={totalRowRef} className="total-amount">
            <AnimatedCents cents={amountDueCents} />
          </span>
        </span>
      </p>
      {/*
        What the subscription actually bills from the second cleaning on.
        The nearby discount is earned by one date's neighbourhood, so Stripe
        takes it off the first invoice alone (a `duration: "once"` coupon) and
        charges this every cycle after — see `ongoingPrice`, which computes it
        the same way the server does. Saying it here is what keeps the
        discount an incentive rather than a surprise.
      */}
      {ongoingCents !== null ? (
        <p className="cart-price-note">
          Then {formatCents(ongoingCents)} per cleaning. Your nearby discount
          applies to the first one.
        </p>
      ) : null}
      {hasServiceDate ? null : (
        /**
         * Before a date is chosen the total is priced with every day-dependent
         * axis collapsed (see `computeBookingPrice`), so it is a floor rather
         * than a quote — a busy day adds its surge, and a day with no other
         * cleaning nearby its travel fee, once the date is picked.
         * Saying so here is what makes the number honest; the alternative is a
         * total that silently goes up between two steps of the funnel.
         */
        <p className="cart-price-note">
          {/*
            "each day's price" until the calendar stopped printing one. The
            promise has to match what the next step actually shows, or the
            customer arrives looking for thirty numbers that are not there.
          */}
          Final price depends on the day and time you choose. Pick a date on the
          next step to see it.
        </p>
      )}
    </CartContainer>
  );
};
/**
 * The blended rate, and nothing else.
 *
 * Built from the brand tokens in index.css — a tinted wash of --blue-300 on the
 * cart's pale cyan, so the saving reads as its own object without introducing a
 * colour the rest of the flow does not use.
 *
 * It used to open the itemisation on hover, click or focus. That itemisation is
 * now permanently underneath it, so the affordances went with it: the caret,
 * the hover lift and the tab stop were all promising something to open.
 */
const SavingsPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 10px;
  border-radius: 999px;
  background: rgba(37, 62, 198, 0.1);
  color: var(--blue-300);
  font-family: "Palanquin", "Poppins", sans-serif;
  font-weight: 600;
  font-size: 0.82rem;
  line-height: 1.5;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

/**
 * The discount and the lines that make it up, as one object.
 *
 * The wash lands on the head row rather than the whole block: the rows below
 * announce themselves individually as they arrive, and washing both at once
 * turns a single change into a card-sized flash of green.
 */
const DiscountBlock = styled.div`
  @keyframes cart-wash {
    from {
      background: ${SAVINGS.tint};
      box-shadow: inset 0 0 0 1px ${SAVINGS.edge};
    }
    to {
      background: transparent;
      box-shadow: inset 0 0 0 1px transparent;
    }
  }

  @keyframes cart-pill-pop {
    50% {
      transform: scale(1.12);
    }
  }

  .discount-head {
    padding: 2px 6px;
    margin: 0 -6px;
    border-radius: 6px;
  }

  &.washing .discount-head {
    animation: cart-wash 520ms ease-out both;
  }

  &.washing ${SavingsPill} {
    animation: cart-pill-pop 320ms cubic-bezier(0.3, 1.4, 0.5, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    &.washing .discount-head,
    &.washing ${SavingsPill} {
      animation: none;
    }
  }
`;

/**
 * The itemisation, on the face of the card.
 *
 * Structurally the same list it was inside the tooltip — a typed tag, a label
 * and an amount — restated in the cart's own inks now that it sits on pale cyan
 * rather than on indigo. Green for the amounts, because every one of them is a
 * saving; #15803d is the shade `savings.ts` already vouches for at this size.
 */
const Ledger = styled.ul`
  margin: 1px 0 3px;
  padding: 0;
  list-style: none;
  font-variant-numeric: tabular-nums;

  @keyframes ledger-enter {
    from {
      opacity: 0;
      transform: translateY(-3px);
      background: ${SAVINGS.tint};
    }
    60% {
      opacity: 1;
      transform: none;
      background: ${SAVINGS.tint};
    }
    to {
      opacity: 1;
      transform: none;
      background: transparent;
    }
  }

  /* A departing saving collapses rather than blinking away, so the total it
     was part of is seen counting back up from the place it left.

     Collapsed by closing the row's own grid track rather than by animating
     down from a height picked in advance. A label wraps to two lines whenever
     the sidebar is narrow, so any fixed starting height is either a clip on
     the rows that wrap or dead time at the front of the ones that do not. A
     track of 1fr is whatever the row actually measures. */
  @keyframes ledger-exit {
    from {
      opacity: 1;
      grid-template-rows: 1fr;
    }
    to {
      opacity: 0;
      grid-template-rows: 0fr;
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  li {
    display: grid;
    grid-template-rows: 1fr;
    padding: 3px 6px 3px 18px;
    margin: 0 -6px;
    border-radius: 6px;
    font-size: 0.775rem;
    line-height: 1.45;
  }

  li > .line {
    display: flex;
    align-items: baseline;
    gap: 9px;
    /* Both are what let the track close over the content rather than be held
       open by it. */
    min-height: 0;
    overflow: hidden;
  }

  li.entering {
    animation: ledger-enter 520ms ease-out backwards;
  }

  li.exiting {
    overflow: hidden;
    animation: ledger-exit 240ms ease-in both;
  }

  /* The category, not a sequence: these are different kinds of saving, so a
     typed tag carries information a 01/02/03 marker would not. */
  .line > em {
    flex: none;
    width: 34px;
    font-style: normal;
    font-size: 0.56rem;
    font-weight: 600;
    letter-spacing: 0.09em;
    color: ${SAVINGS.ink};
    opacity: 0.8;
  }

  /* The label alone. The amount is a rolling number, which puts a span of its
     own inside the <b>, and an unscoped rule for spans reaches that one too —
     repainting the saving in the label's slate, flex-grown, instead of green. */
  .line > .label {
    flex: 1;
    min-width: 0;
    color: #3d4166;
    /* Every label is one line at every width the cart renders at. This is the
       guard for the one that is not — a promo code long enough to need it
       breaks inside itself rather than pushing the amount off the card. */
    overflow-wrap: anywhere;
  }

  .line > b {
    flex: none;
    white-space: nowrap;
    font-weight: 600;
    color: ${SAVINGS.ink};
  }

  @media (prefers-reduced-motion: reduce) {
    li.entering,
    li.exiting {
      animation: none;
    }
  }
`;
